import React from 'react';
import { ChevronDown, BarChart3, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import type { FilterMode } from '@/types/trace';

interface SidebarHeaderProps {
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
}

export function SidebarHeader({ onExpandAll, onCollapseAll }: SidebarHeaderProps) {
  const {
    state,
    setViewMode,
    setFilterMode,
    setDisplaySettings,
  } = useTraceSidebar();

  const filterModeLabels: Record<FilterMode, string> = {
    most_relevant: 'Most Relevant',
    filtered_only: 'Filtered Only',
    show_all: 'Show All',
  };

  return (
    <div className="flex items-center justify-between p-2 border-b bg-card">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-0.5">
        <Button
          variant={state.viewMode === 'tree' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setViewMode('tree')}
          className="h-7 px-2 text-xs"
        >
          <List className="h-3.5 w-3.5 mr-1" />
          Tree
        </Button>
        <Button
          variant={state.viewMode === 'waterfall' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setViewMode('waterfall')}
          className="h-7 px-2 text-xs"
        >
          <BarChart3 className="h-3.5 w-3.5 mr-1" />
          Waterfall
        </Button>
      </div>

      {/* Filter & Settings - Combined dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
            {filterModeLabels[state.filterMode]}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Filter</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setFilterMode('most_relevant')}>
            Most Relevant
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setFilterMode('filtered_only')}>
            Filtered Only
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setFilterMode('show_all')}>
            Show All
          </DropdownMenuItem>
          {state.viewMode === 'tree' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onCollapseAll}>
                Collapse All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExpandAll}>
                Expand All
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">Display</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={state.displaySettings.showLatency}
            onCheckedChange={(checked) =>
              setDisplaySettings({ showLatency: checked })
            }
          >
            Show Latency
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.displaySettings.showTokens}
            onCheckedChange={(checked) =>
              setDisplaySettings({ showTokens: checked })
            }
          >
            Show Tokens
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.displaySettings.showCost}
            onCheckedChange={(checked) =>
              setDisplaySettings({ showCost: checked })
            }
          >
            Show Cost
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.displaySettings.showTags}
            onCheckedChange={(checked) =>
              setDisplaySettings({ showTags: checked })
            }
          >
            Show Tags
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default SidebarHeader;
